import {
  Box,
  HStack,
  Icon,
  IconButton,
  Stack,
  StackProps,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import {
  ArrowUpIcon,
  BicycleIcon,
  DeleteIcon,
} from "../../../../shared/views/icons";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  Score,
} from "@keita/lanepatrol-sdk";
import { LightSource } from "./LightSource";
import {
  backgroundGradient,
  cycleGreenColor,
  cycleRedColor,
  multiUsePathGradient,
  roadLaneColor,
  roadShoulderColor,
  segregationColor,
  sidewalkGradient,
} from "./constants";
import { CrossSectionAdjacentFeatures } from "./CrossSectionAdjacentFeatures";
import { CreateOrUpdateHlaLane } from "../../../models/HlaLane";
import { CreateOrUpdateSimulationLane } from "../../../models/SimulationLane";
import { HlaScoreEntryView } from "../../HlaScoreView";

type CrossSectionLane = CreateOrUpdateHlaLane | CreateOrUpdateSimulationLane;

const CrossSectionViewBase = ({
  lit,
  lane,
  canEdit,
  isSelected,
  score,
  onSelect,
  onDelete,
}: {
  lit: boolean;
  lane: CrossSectionLane;
  canEdit: boolean;
  isSelected: boolean;
  score?: Score | null;
  onSelect: () => void;
  onDelete: () => void;
}) => {
  const showTwoLanes =
    lane.flowDirection === FlowDirectionType.TwoWay &&
    lane.facilityType !== FacilityType.Sidewalk;

  const widthPerDirection =
    facilityWidthMultiplier(lane.facilityWidthPerDirection) * 30;

  const mainLaneProps: StackProps = {};

  if (lane.facilityType === FacilityType.OffRoadBicyclePath) {
    mainLaneProps.bgColor = cycleGreenColor;
  } else if (lane.facilityType === FacilityType.OnRoadBicycleLane) {
    mainLaneProps.bgColor = cycleRedColor;
  } else if (lane.facilityType === FacilityType.MixedTrafficRoadLane) {
    mainLaneProps.bgColor = roadLaneColor;
  } else if (lane.facilityType === FacilityType.RoadShoulder) {
    mainLaneProps.bgColor = roadShoulderColor;
  } else if (lane.facilityType === FacilityType.Sidewalk) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = sidewalkGradient;
  } else if (lane.facilityType === FacilityType.MultiUsePath) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = multiUsePathGradient;
  }

  if (lane.lightSegregation) {
    mainLaneProps.borderLeft = `4px dashed ${segregationColor}`;
    mainLaneProps.borderRight = `4px dashed ${segregationColor}`;
  }

  return (
    <VStack>
      <HlaScoreEntryView score={score?.global} />

      <HStack
        borderWidth={"3px"}
        cursor={isSelected ? "auto" : "pointer"}
        _hover={{ bgColor: "primary.100" }}
        borderColor={isSelected ? "primary.500" : "transparent"}
        position="relative"
        flexShrink={0}
        overflow={"hidden"}
        borderRadius={"8px"}
        onClick={onSelect}
      >
        <HStack
          bgColor="background-color: #e5e5f7"
          bgImage={backgroundGradient}
          backgroundPosition="0 0, 10px 10px"
          bgSize="20px 20px"
          justifyContent={"center"}
          gap={0}
          alignItems="stretch"
          alignSelf="center"
          h={"80px"}
          rotate={lane.frontFacing ? `0deg` : `180deg`}
          overflow={"hidden"}
          borderRadius={"4px"}
        >
          <CrossSectionAdjacentFeatures lane={lane} side="left" />

          <HStack
            gap={1}
            alignItems={"center"}
            transitionDuration={"0.2s"}
            bgColor={"#e7c73dff"}
            {...mainLaneProps}
            flexDir={"row"}
          >
            {showTwoLanes && <Box w={`${widthPerDirection}px`} />}

            {showTwoLanes && <Box borderLeft={"4px dashed white"} h={"full"} />}

            <Stack
              w={`${widthPerDirection}px`}
              h={"full"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={0}
            >
              <Icon color={"white"} as={ArrowUpIcon} />
              <Icon color={"white"} as={BicycleIcon} />
            </Stack>
          </HStack>

          <CrossSectionAdjacentFeatures lane={lane} side="right" />
        </HStack>
        {lit && <LightSource />}
      </HStack>

      <Box h={"40px"}>
        {isSelected && canEdit && (
          <IconButton
            size={"xs"}
            variant={"surface"}
            colorPalette={"primary"}
            onClick={onDelete}
          >
            <Icon as={DeleteIcon} />
          </IconButton>
        )}
      </Box>
    </VStack>
  );
};

export const CrossSectionView = observer(CrossSectionViewBase);

const facilityWidthMultiplier = (option: FacilityWidth | null) => {
  switch (option) {
    case FacilityWidth.VeryNarrow:
      return 1;
    case null:
    case FacilityWidth.Narrow:
      return 2;
    case FacilityWidth.Wide:
      return 3;
  }
};
